import React, { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, ShieldAlert, Loader2, Search, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { adminListInsurance } from '../../services/api';

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('en-IE', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

/**
 * Read-only record of every driver's insurance self-declaration.
 * There is no certificate upload, review or approval step.
 */
export default function InsuranceReviewPanel() {
  const [data, setData] = useState({ items: [], counts: { total: 0, declared: 0, not_declared: 0 } });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const load = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const { data: res } = await adminListInsurance(q);
      setData(res);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const items = data.items.filter((d) =>
    filter === 'all' ? true : filter === 'declared' ? d.declared : !d.declared
  );

  const chip = (key, label, count) => (
    <button
      key={key}
      onClick={() => setFilter(key)}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
        filter === key
          ? 'bg-[#1B5E20] text-white border-[#1B5E20]'
          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
      }`}
      data-testid={`insurance-filter-${key}`}
    >
      {label} <span className="opacity-70">{count}</span>
    </button>
  );

  return (
    <Card className="border border-slate-200 rounded-lg shadow-none" data-testid="insurance-review-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-[#1B5E20]" strokeWidth={2} />
          Driver Insurance Declarations
        </CardTitle>
        <p className="text-sm text-slate-500">
          Drivers self-declare that they hold valid insurance for the deliveries they carry out. Record only — no documents are collected.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          {chip('all', 'All drivers', data.counts.total)}
          {chip('declared', 'Declared', data.counts.declared)}
          {chip('not_declared', 'Not declared', data.counts.not_declared)}
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') load(search); }}
              placeholder="Search driver name, email or phone…"
              className="pl-9 h-10"
              data-testid="insurance-search-input"
            />
          </div>
          <Button variant="outline" onClick={() => load(search)} className="gap-2 h-10" data-testid="insurance-refresh-btn">
            <RefreshCw className="w-4 h-4" /> Refresh
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading declarations…
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-500 py-6">No drivers match this view.</p>
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-hidden divide-y divide-slate-100">
            {items.map((d) => (
              <div key={d.driver_id} className="flex items-center justify-between gap-3 px-4 py-3 bg-white" data-testid={`insurance-row-${d.driver_id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{d.driver_name || 'Unnamed driver'}</p>
                  <p className="text-xs text-slate-500 truncate">{d.driver_email}{d.driver_phone ? ` · ${d.driver_phone}` : ''}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  {d.declared ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-[#1B5E20] border border-emerald-200 text-xs font-semibold">
                      <ShieldCheck className="w-3.5 h-3.5" /> Declared
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-xs font-semibold">
                      <ShieldAlert className="w-3.5 h-3.5" /> Not declared
                    </span>
                  )}
                  <p className="text-[11px] text-slate-500 mt-1">{d.declared ? fmt(d.declared_at) : 'Cannot place bids'}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
